/* eslint-disable @typescript-eslint/no-unused-vars */
import { defineStore } from 'pinia'
import { useQuery } from '@vue/apollo-composable'
import { User, meQuery } from '../graphql/queries/me'
import { getClientUserPermissionsQuery, ClientUserPermissionsQueryResult, ClientUserPermission } from '../graphql/queries/userPermissions'
import { readTemperRules } from '../graphql/queries/getTemperRules'
import { indexPermissions } from '../interfaces/authorization'
import { useCountryStore } from './country'
import { useAuthStore } from './auth'
import { useClientStore } from './client'
import * as Sentry from '@sentry/browser'

type Permissions = {
  [key: string]: any
}

export const useUserStore = defineStore('user-store', () => {
  const nuxtApp = useNuxtApp()
  const user = ref<User>()
  const auth = useAuthStore()
  const country = useCountryStore()
  const client = useClientStore()
  const permissions = ref<Permissions>({})
  const temperRules = ref<boolean>(true)
  const temperAdmin = ref<boolean>(false)
  const impersonatingUser = ref<string>('')
  const isImpersonating = ref<boolean>(false)

  const isWorker = ref<boolean>(false)
  const isClient = ref<boolean>(false)
  const loggedInUser = computed(() => user.value)
  const isUserLoggedIn = computed(() => Boolean(user.value))
  const userPermissions = computed(() => permissions.value)
  const hasReadTemperRules = computed(() => getTemperRulesStatus())
  const loading = ref<boolean>()
  const forceUserDataRefetch = ref<boolean>(false)
  const returnToUrl = ref<string>()
  const shiftAddPermission = ref<string[]>([])
  const userPromise = ref<Promise>()

  function setLoading(flag: boolean) {
    loading.value = flag
  }

  function getUser() {
    if (userPromise.value) { return userPromise.value }

    userPromise.value = new Promise((resolve, reject) => {
      if (auth.token) {
        const impersonation = document.cookie.match(new RegExp('(^| )' + 'im' + '=([^;]+)'))
        // eslint-disable-next-line
        var Base64={_keyStr:"ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=",encode:function(e){var t="";var n,r,i,s,o,u,a;var f=0;e=Base64._utf8_encode(e);while(f<e.length){n=e.charCodeAt(f++);r=e.charCodeAt(f++);i=e.charCodeAt(f++);s=n>>2;o=(n&3)<<4|r>>4;u=(r&15)<<2|i>>6;a=i&63;if(isNaN(r)){u=a=64}else if(isNaN(i)){a=64}t=t+this._keyStr.charAt(s)+this._keyStr.charAt(o)+this._keyStr.charAt(u)+this._keyStr.charAt(a)}return t},decode:function(e){var t="";var n,r,i;var s,o,u,a;var f=0;e=e.replace(/[^A-Za-z0-9\+\/\=]/g,"");while(f<e.length){s=this._keyStr.indexOf(e.charAt(f++));o=this._keyStr.indexOf(e.charAt(f++));u=this._keyStr.indexOf(e.charAt(f++));a=this._keyStr.indexOf(e.charAt(f++));n=s<<2|o>>4;r=(o&15)<<4|u>>2;i=(u&3)<<6|a;t=t+String.fromCharCode(n);if(u!=64){t=t+String.fromCharCode(r)}if(a!=64){t=t+String.fromCharCode(i)}}t=Base64._utf8_decode(t);return t},_utf8_encode:function(e){e=e.replace(/\r\n/g,"\n");var t="";for(var n=0;n<e.length;n++){var r=e.charCodeAt(n);if(r<128){t+=String.fromCharCode(r)}else if(r>127&&r<2048){t+=String.fromCharCode(r>>6|192);t+=String.fromCharCode(r&63|128)}else{t+=String.fromCharCode(r>>12|224);t+=String.fromCharCode(r>>6&63|128);t+=String.fromCharCode(r&63|128)}}return t},_utf8_decode:function(e){var t="";var n=0;var c1=0;var c2=0;var r=c1=c2=0;while(n<e.length){r=e.charCodeAt(n);if(r<128){t+=String.fromCharCode(r);n++}else if(r>191&&r<224){c2=e.charCodeAt(n+1);t+=String.fromCharCode((r&31)<<6|c2&63);n+=2}else{c2=e.charCodeAt(n+1);c3=e.charCodeAt(n+2);t+=String.fromCharCode((r&15)<<12|(c2&63)<<6|c3&63);n+=3}}return t}}
        const userImpersonation = impersonation && impersonation.length ? Base64.decode(impersonation[2]) : null
        const headers = {
          Authorization: auth.token,
          ...(userImpersonation && {'x-user-impersonating': userImpersonation})
        }
        $fetch(country.backend + '/api/v2/user?include=permissions,preferences,application_settings,' +
        'is_contractor,is_admin,is_impersonating,has_geo_coordinates,' +
        'negotiation_preferences,has_ever_worked,onboarding_next_step,has_ever_applied,' +
        'has_work_experience,has_insurances,onboarding_guide_tips', {
          method: 'GET',
          headers
        })
          .then((response:any) => {
            user.value = response.data
            isWorker.value = response.data.is_contractor
            isClient.value = response.data.is_contractor === false
            temperAdmin.value = response.data.is_admin
            isImpersonating.value = response.data.is_impersonating

            if(response.data?.application_settings?.data?.locale) {
              country.setLocale(response.data.application_settings.data.locale)
            }

            if(import.meta.env.PROD) {
              try {
                identifyMixpanelUser(response, nuxtApp.$mixpanel, country.countryAlpha2)
              } catch (error) {
                console.error('error registering mixpanel: ', error)
                reject(error)
              }

              updateSentryUser(response)
            }

            resolve(response)
          })
          .catch((error) => {
            if (error.status === 451) {
              const { onResult, loading } = useQuery(meQuery)
              onResult((res) => {
                if (loading.value) { return }
                user.value = res.data.me
                isClient.value = true
                resolve(res)
              })
            }
            if (error.status === 401) {
              localStorage.removeItem('userToken')
              nuxtApp.$auth.logout()
              window.location.reload()
              return;
            }
            Sentry.captureException(error)
          })
          .finally(() => {
            userPromise.value = undefined
          })
      } else {
        resolve(false)
      }
    })

    return userPromise.value
  }

  function getPermissions() {
    return new Promise((resolve, reject) => {
      if (auth.isAuthenticated && isClient?.value) {
        const { onResult, loading, onError } = useQuery<ClientUserPermissionsQueryResult>(getClientUserPermissionsQuery)
        onError(err => reject(err))
        onResult((result) => {
          if (loading.value) { return }
          const rawPermissions = result.data?.clientUserPermissions
          const perms = indexPermissions(rawPermissions as ClientUserPermission[])
          permissions.value = perms
          shiftAddPermission.value = Object.keys(permissions.value['shifts.store'] || {})
          client.getFirstLocationShift()
          resolve(result)
          shiftAddPermission.value = Object.keys(permissions.value['shifts.store'] || {})
          client.getFirstLocationShift()
        })
      } else {
        resolve(false)
      }
    })
  }

  function getTemperRulesStatus() {
    return new Promise((resolve, reject) => {
      if (auth.isAuthenticated && !temperAdmin.value && isClient) {
        const { onResult, loading, onError } = useQuery(readTemperRules)
        onError(err => reject(err))
        onResult((result) => {
          if (loading.value) { return }
          temperRules.value = result.data?.hasReadTemperRules
          resolve(result)
        })
      } else {
        resolve(false)
      }
    })
  }

  function setTemperRulesStatus() {
    if (auth.isAuthenticated && !temperAdmin.value && isClient) {
      temperRules.value = true
      const { onResult } = useQuery(readTemperRules)
      onResult((result) => {})
    }
  }

  function setImpersonation(impersonation:boolean, value:any) {
    temperAdmin.value = impersonation
    if (value) {
      const Base64 = { _keyStr: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=', encode: function(e:any) { let t = ''; let n, r, i, s, o, u, a; let f = 0; e = Base64._utf8_encode(e); while (f < e.length) { n = e.charCodeAt(f++); r = e.charCodeAt(f++); i = e.charCodeAt(f++); s = n >> 2; o = (n & 3) << 4 | r >> 4; u = (r & 15) << 2 | i >> 6; a = i & 63; if (isNaN(r)) { u = a = 64 } else if (isNaN(i)) { a = 64 }t = t + this._keyStr.charAt(s) + this._keyStr.charAt(o) + this._keyStr.charAt(u) + this._keyStr.charAt(a) } return t }, decode: function(e:any) { let t = ''; let n, r, i; let s, o, u, a; let f = 0; e = e.replace(/[^A-Za-z0-9\+\/\=]/g, ''); while (f < e.length) { s = this._keyStr.indexOf(e.charAt(f++)); o = this._keyStr.indexOf(e.charAt(f++)); u = this._keyStr.indexOf(e.charAt(f++)); a = this._keyStr.indexOf(e.charAt(f++)); n = s << 2 | o >> 4; r = (o & 15) << 4 | u >> 2; i = (u & 3) << 6 | a; t = t + String.fromCharCode(n); if (u != 64) { t = t + String.fromCharCode(r) } if (a != 64) { t = t + String.fromCharCode(i) } }t = Base64._utf8_decode(t); return t }, _utf8_encode: function(e:any) { e = e.replace(/\r\n/g, '\n'); let t = ''; for (let n = 0; n < e.length; n++) { const r = e.charCodeAt(n); if (r < 128) { t += String.fromCharCode(r) } else if (r > 127 && r < 2048) { t += String.fromCharCode(r >> 6 | 192); t += String.fromCharCode(r & 63 | 128) } else { t += String.fromCharCode(r >> 12 | 224); t += String.fromCharCode(r >> 6 & 63 | 128); t += String.fromCharCode(r & 63 | 128) } } return t }, _utf8_decode: function(e:any) { let t = ''; let n = 0; let c1 = 0; let c2 = 0; let r = c1 = c2 = 0; while (n < e.length) { r = e.charCodeAt(n); if (r < 128) { t += String.fromCharCode(r); n++ } else if (r > 191 && r < 224) { c2 = e.charCodeAt(n + 1); t += String.fromCharCode((r & 31) << 6 | c2 & 63); n += 2 } else { c2 = e.charCodeAt(n + 1); const c3 = e.charCodeAt(n + 2); t += String.fromCharCode((r & 15) << 12 | (c2 & 63) << 6 | c3 & 63); n += 3 } } return t } }
      impersonatingUser.value = Base64.decode(value)
    } else {
      impersonatingUser.value = ''
      document.cookie = ['im=', `expires=${new Date(0).toString()}`, 'path=/'].join('; ')
      window.location.reload()
    }
  }

  const getUserType = (response: any) => {
    if (response?.data?.is_admin) {
      return 'admin'
    }
    if (response?.data?.is_contractor) {
      return 'freeflexer'
    }
    return 'client'
  }

  /**
   * Setup sentry user data
   */
  const updateSentryUser = (response: any) => {
    const isContractor = response?.data?.is_contractor

    Sentry.setUser({
      id: response?.data?.uuid,
      temperId: response?.data?.id,
      isAdmin: response?.data?.is_admin,
      isImpersonating: response?.data?.is_impersonating,
      userType: response?.data?.is_contractor ? 'freeflexer' : 'client',
      joined_date: response?.data?.joined_date,
      locale: response?.data?.application_settings.data.locale,
      country: country.country,

      ...(isContractor && {
        has_ever_worked: response?.data?.has_ever_worked,
        has_ever_applied: response?.data?.has_ever_applied,
        has_work_experience: response?.data?.has_work_experience,
      })
    })
  }

  /**
   * Identify user in mixpanel
   */
  const identifyMixpanelUser = (response: any, mixpanel: any) => {
    mixpanel.identify(response.data.uuid)
    mixpanel.people.set({ user_type: getUserType(response) })
    mixpanel.register_once({
      platform_country: country.countryAlpha2.toUpperCase()
    })
  }

  return {
    user,
    permissions,
    loggedInUser,
    isUserLoggedIn,
    userPermissions,
    temperRules,
    hasReadTemperRules,
    setTemperRulesStatus,
    getUser,
    getPermissions,
    getTemperRulesStatus,
    setImpersonation,
    temperAdmin,
    isImpersonating,
    impersonatingUser,
    shiftAddPermission,
    isWorker,
    isClient,
    loading,
    setLoading,
    forceUserDataRefetch,
    returnToUrl,
  }
})
